// apps/client/src/pages/leads/CrmStageMappingsPage.tsx
//
// PR-3 chunk 5 — admin UI for the per-tenant CRM stage override doc
// at companies/{id}/crm_stage_overrides/{provider}.
//
// Flips a custom CRM status (e.g. "Submitted to Lender (CUSTOM)") to a
// unified pipeline stage without shipping code. Reading is open to any
// signed-in member; saving is super_admin / administrator only.
//
// Layout
// ------
//   [PageHeader] ...................................... [Save Mappings]
//   [Tab strip: mycrm | nodify | monday | smokeball]
//   ┌───────────────────────────────┬──────────────────────────────┐
//   │ Canonical Map (read-only)     │ Your Overrides (editable)    │
//   │ raw status → unified stage    │ rawStatus | stage | x        │
//   │ ...                           │ + Add row                    │
//   └───────────────────────────────┴──────────────────────────────┘

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  AlertTriangle,
  Cog,
  Plus,
  RefreshCw,
  Save,
  Trash2,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
// Sub-path import — bypasses the shared-schemas barrel which transitively
// loads brokerIdentity + loanFingerprinting (both Node-only via crypto).
// Webpack 5 (CRA) doesn't polyfill crypto, so the barrel breaks the
// frontend bundle. Same pattern as @valutten/shared-utils/auth.
import {
  CRM_PROVIDERS,
  type CrmProvider,
  type UnifiedStage,
  UNIFIED_STAGES,
  rawStatusesForUnifiedStage,
  toUnifiedStage,
} from '@valutten/shared-schemas/dist/crmStageMapping';
import { functions } from '../../lib/firebase';
import { PageHeader } from '../../components/common/PageHeader';
import { CrmClassificationMapEditor } from '../../components/leads/crmClassificationMap/CrmClassificationMapEditor';

type OutletCtx = {
  userData?: { uid?: string; companyId?: string; role?: string };
};

interface OverrideRow {
  // Local row state. rawStatus is freeform until save (lowercased
  // server-side to match toUnifiedStageWithOverrides normalisation).
  rawStatus: string;
  unifiedStage: UnifiedStage;
}

interface GetOverrideResponse {
  mappings: Record<string, UnifiedStage>;
  isEmpty: boolean;
}

const CrmStageMappingsPage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId ?? '';
  const role = userData?.role ?? '';
  // Marketing (Lead-Capture operator) is granted access to the 4 Lead-Capture
  // config tabs alongside super_admin / administrator (marketing RBAC §1 GRANT).
  const isAdmin = role === 'super_admin' || role === 'administrator' || role === 'marketing';

  const [activeProvider, setActiveProvider] =
    useState<CrmProvider>('mycrm');

  // Per-provider local state — keyed by provider so flipping tabs
  // doesn't lose pending edits in the other tabs. Reload from server on
  // tab switch only if we haven't loaded yet.
  const [rowsByProvider, setRowsByProvider] = useState<
    Record<CrmProvider, OverrideRow[]>
  >({
    mycrm: [],
    nodify: [],
    monday: [],
    smokeball: [],
  });
  const [loadedProviders, setLoadedProviders] = useState<
    Set<CrmProvider>
  >(new Set());

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // ── Canonical map for the active provider ──────────────────────────
  // Iterates rawStatusesForUnifiedStage for each unified stage to
  // enumerate the shipped per-provider map without touching the private
  // REGISTRY const inside crmStageMapping.ts.
  const canonicalEntries = useMemo<
    Array<{ rawStatus: string; unifiedStage: UnifiedStage }>
  >(() => {
    const out: Array<{ rawStatus: string; unifiedStage: UnifiedStage }> = [];
    for (const stage of UNIFIED_STAGES) {
      if (stage === 'unknown') continue; // 'unknown' is a fallback, not a mapping
      for (const raw of rawStatusesForUnifiedStage(activeProvider, stage)) {
        out.push({ rawStatus: raw, unifiedStage: stage });
      }
    }
    // Sort: by unified stage ordinal then raw alphabetic.
    const order = new Map<UnifiedStage, number>(
      UNIFIED_STAGES.map((s, i) => [s, i] as const),
    );
    out.sort((a, b) => {
      const so = (order.get(a.unifiedStage) ?? 0) - (order.get(b.unifiedStage) ?? 0);
      if (so !== 0) return so;
      return a.rawStatus.localeCompare(b.rawStatus);
    });
    return out;
  }, [activeProvider]);

  // ── Load on provider switch (cached) ───────────────────────────────
  const loadProvider = useCallback(
    async (provider: CrmProvider) => {
      if (!companyId) return;
      if (loadedProviders.has(provider)) return;
      setLoading(true);
      setError(null);
      try {
        const fn = httpsCallable<unknown, GetOverrideResponse>(
          functions,
          'leadCaptureAction',
        );
        const r = await fn({
          action: 'getCrmStageOverride',
          companyId,
          provider,
        });
        const rows: OverrideRow[] = Object.entries(r.data.mappings ?? {}).map(
          ([raw, stage]) => ({ rawStatus: raw, unifiedStage: stage }),
        );
        setRowsByProvider((m) => ({ ...m, [provider]: rows }));
        setLoadedProviders((s) => {
          const next = new Set(s);
          next.add(provider);
          return next;
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [companyId, loadedProviders],
  );

  useEffect(() => {
    void loadProvider(activeProvider);
  }, [activeProvider, loadProvider]);

  // ── Mutations ──────────────────────────────────────────────────────
  const updateRow = useCallback(
    (idx: number, patch: Partial<OverrideRow>) => {
      setRowsByProvider((m) => {
        const next = [...(m[activeProvider] ?? [])];
        next[idx] = { ...next[idx], ...patch };
        return { ...m, [activeProvider]: next };
      });
    },
    [activeProvider],
  );

  const addRow = useCallback(() => {
    setRowsByProvider((m) => ({
      ...m,
      [activeProvider]: [
        ...(m[activeProvider] ?? []),
        { rawStatus: '', unifiedStage: 'qualified' },
      ],
    }));
  }, [activeProvider]);

  const removeRow = useCallback(
    (idx: number) => {
      setRowsByProvider((m) => ({
        ...m,
        [activeProvider]: (m[activeProvider] ?? []).filter((_, i) => i !== idx),
      }));
    },
    [activeProvider],
  );

  const save = useCallback(async () => {
    if (!companyId) return;
    setSaving(true);
    setError(null);
    setSavedAt(null);

    const rows = rowsByProvider[activeProvider] ?? [];
    // Reject empty raw statuses + duplicates (last write wins is too
    // surprising for an admin form). Surface as a friendly inline error.
    const seen = new Set<string>();
    const mappings: Record<string, UnifiedStage> = {};
    for (const row of rows) {
      const key = row.rawStatus.trim().toLowerCase();
      if (!key) {
        setError('Every override row needs a raw status — remove blank rows or fill them in.');
        setSaving(false);
        return;
      }
      if (seen.has(key)) {
        setError(`Duplicate raw status "${key}" — remove or rename one row.`);
        setSaving(false);
        return;
      }
      seen.add(key);
      mappings[key] = row.unifiedStage;
    }

    try {
      const fn = httpsCallable<unknown, { success: boolean }>(
        functions,
        'leadCaptureAction',
      );
      await fn({
        action: 'saveCrmStageOverride',
        companyId,
        provider: activeProvider,
        mappings,
      });
      setSavedAt(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [activeProvider, companyId, rowsByProvider]);

  // ── Renders ────────────────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={Cog}
          title="CRM Mappings"
          subtitle="super_admin / administrator only"
        />
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-amber-300 text-sm">
          You don&apos;t have permission to view this page.
        </div>
      </div>
    );
  }

  const rows = rowsByProvider[activeProvider] ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Cog}
        title="CRM Mappings"
        subtitle="Map your CRM's custom statuses to the unified pipeline stages used across reports. Overrides are applied when the Pipeline board is read, so a saved change shows on the next refresh."
        badge="Beta"
        actions={
          <button
            onClick={save}
            // Save stays disabled until THIS provider has loaded
            // successfully. The page starts from an empty row set, so a
            // failed load left Save enabled over an empty form: one click
            // then wrote an empty map and wiped the tenant's overrides.
            disabled={saving || loading || !loadedProviders.has(activeProvider)}
            title={
              loadedProviders.has(activeProvider)
                ? undefined
                : 'Waiting for the current mappings to load'
            }
            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saving ? 'Saving…' : 'Save Mappings'}
          </button>
        }
      />

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {savedAt && Date.now() - savedAt < 3000 && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-300 text-sm">
          Mappings saved.
        </div>
      )}

      {/* ── Provider tabs ──────────────────────────────────────────── */}
      <div className="flex gap-1 border-b border-gray-800">
        {CRM_PROVIDERS.map((p) => {
          const isActive = p === activeProvider;
          return (
            <button
              key={p}
              onClick={() => setActiveProvider(p)}
              className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors -mb-px border ${
                isActive
                  ? 'bg-blue-500/15 text-blue-300 border-blue-500/30 border-b-transparent'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/60 border-transparent'
              }`}
            >
              {p === 'mycrm'
                ? 'MyCRM'
                : p === 'nodify'
                ? 'Nodify'
                : p === 'monday'
                ? 'Monday'
                : 'Smokeball'}
            </button>
          );
        })}
      </div>

      {/* ── Two-column body ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: canonical (read-only) ─────────────────────────────── */}
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-5">
          <div className="flex justify-between items-baseline mb-3">
            <h3 className="text-sm uppercase tracking-wider text-gray-400 font-semibold">
              Canonical Map (read-only)
            </h3>
            <span className="text-[11px] text-gray-500">
              shipped with the platform
            </span>
          </div>

          {canonicalEntries.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-6">
              No canonical mappings shipped for this CRM yet.
              <br />
              <span className="text-xs text-gray-500">
                Add rows on the right to define your own.
              </span>
            </div>
          ) : (
            <div className="space-y-1">
              {canonicalEntries.map((entry) => (
                <div
                  key={`${entry.rawStatus}-${entry.unifiedStage}`}
                  className="grid grid-cols-12 gap-2 items-center text-xs px-2 py-1.5 rounded hover:bg-gray-800/40"
                >
                  <span className="col-span-7 font-mono text-gray-200 truncate">
                    {entry.rawStatus}
                  </span>
                  <span className="col-span-1 text-gray-600 text-center">
                    →
                  </span>
                  <span className="col-span-4">
                    <UnifiedStagePill stage={entry.unifiedStage} />
                  </span>
                </div>
              ))}
            </div>
          )}

          <p className="mt-4 text-[11px] text-gray-500 leading-relaxed">
            These are the platform defaults. Anything below applied via your
            overrides on the right takes precedence.{' '}
            <span className="font-mono">
              toUnifiedStage(&apos;{activeProvider}&apos;, …)
            </span>
            {' '}is the lookup function used by every report.
          </p>
        </div>

        {/* RIGHT: editable overrides ───────────────────────────────── */}
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm uppercase tracking-wider text-gray-400 font-semibold">
              Your Overrides (editable)
            </h3>
            <button
              onClick={addRow}
              className="px-3 py-1.5 rounded-md bg-gray-800 border border-gray-700 hover:bg-gray-700 text-white text-xs inline-flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add Row
            </button>
          </div>

          {rows.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-8">
              No overrides yet for {activeProvider}.
              <br />
              <span className="text-xs text-gray-500">
                Add a row to redirect a custom status to a unified stage.
              </span>
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map((row, idx) => {
                const trimmedKey = row.rawStatus.trim().toLowerCase();
                // Show what the canonical map would have done before
                // override — helps the admin sanity-check intent.
                const canonical = trimmedKey
                  ? toUnifiedStage(activeProvider, trimmedKey)
                  : null;
                const overridesCanonical =
                  canonical !== null && canonical !== row.unifiedStage;
                return (
                  <div
                    key={idx}
                    className="grid grid-cols-12 gap-2 items-center"
                  >
                    <input
                      type="text"
                      placeholder="Raw status (e.g. Submitted to Lender (CUSTOM))"
                      value={row.rawStatus}
                      onChange={(e) =>
                        updateRow(idx, { rawStatus: e.target.value })
                      }
                      className="col-span-6 bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1.5 text-xs font-mono"
                    />
                    <select
                      value={row.unifiedStage}
                      onChange={(e) =>
                        updateRow(idx, {
                          unifiedStage: e.target.value as UnifiedStage,
                        })
                      }
                      className="col-span-5 bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1.5 text-xs"
                    >
                      {UNIFIED_STAGES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => removeRow(idx)}
                      className="col-span-1 text-gray-400 hover:text-red-400 p-1 justify-self-end"
                      title="Remove row"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    {overridesCanonical && canonical && (
                      <div className="col-span-12 -mt-1 ml-1 text-[10px] text-amber-300/80">
                        overrides canonical{' '}
                        <span className="font-mono">{canonical}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <p className="mt-4 text-[11px] text-gray-500 leading-relaxed">
            Saved to{' '}
            <span className="font-mono">
              companies/{`{id}`}/crm_stage_overrides/{activeProvider}
            </span>
            . Raw statuses are normalised to lowercase + trimmed on save —
            casing variance from the upstream CRM is handled automatically.
          </p>
        </div>
      </div>

      {/* Lead classification -> CRM value map (website forms platform, W7). */}
      <CrmClassificationMapEditor companyId={companyId || undefined} canWrite={isAdmin} />

      {loading && (
        <div className="text-center text-gray-400 text-sm py-4">
          Loading overrides…
        </div>
      )}
    </div>
  );
};

// ── helpers ──────────────────────────────────────────────────────────

const UnifiedStagePill: React.FC<{ stage: UnifiedStage }> = ({ stage }) => {
  const palette: Record<UnifiedStage, string> = {
    new: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
    contacted: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
    qualified: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    deal_won: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    deal_lost: 'bg-red-500/15 text-red-300 border-red-500/30',
    unknown: 'bg-gray-500/15 text-gray-300 border-gray-500/30',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${palette[stage]}`}
    >
      {stage}
    </span>
  );
};

export default CrmStageMappingsPage;
